type Props = Record<string, unknown>;

type GtagFunction = (...args: unknown[]) => void;

type PostHogClient = {
  capture?: (event: string, properties?: Props) => void;
  identify?: (distinctId: string, properties?: Props) => void;
};

type AnalyticsWindow = typeof globalThis & {
  __GA_LOADED__?: boolean;
  dataLayer?: unknown[];
  gtag?: GtagFunction;
  posthog?: PostHogClient;
};

const googleAnalyticsId = process.env.REACT_APP_GA_MEASUREMENT_ID;

function sanitizeProps(
  input?: Props,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (!input) {
    return out;
  }

  for (const [k, v] of Object.entries(input)) {
    if (
      typeof v === 'string' ||
      typeof v === 'number' ||
      typeof v === 'boolean'
    ) {
      out[k] = v;
    }
  }

  return out;
}

export function initAnalytics(): void {
  try {
    const w = globalThis as AnalyticsWindow;

    if (!googleAnalyticsId) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug(
          '[analytics] GA disabled — set REACT_APP_GA_MEASUREMENT_ID to enable',
        );
      }

      return;
    }

    // Avoid duplicate injection
    if (w.__GA_LOADED__ ?? typeof w.gtag === 'function') {
      return;
    }

    // Prepare GA queue before script loads
    const dataLayer = w.dataLayer ?? [];
    w.dataLayer = dataLayer;
    w.gtag = (...args: unknown[]): void => {
      dataLayer.push(args);
    };

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(googleAnalyticsId)}`;
    script.addEventListener('load', () => {
      w.__GA_LOADED__ = true;
      const {gtag} = w;

      gtag?.('js', new Date());
      // Disable auto page_view; SPA will send manually
      gtag?.('config', googleAnalyticsId, {send_page_view: false}); // eslint-disable-line @typescript-eslint/naming-convention

      if (process.env.NODE_ENV === 'production') {
        console.log('[analytics] Analytics loaded');
      } else {
        console.debug('[analytics] GA4 loaded', {GA_ID: googleAnalyticsId}); // eslint-disable-line @typescript-eslint/naming-convention
      }

      try {
        trackPageview();
      } catch {}
    });

    document.head.append(script);
  } catch {
    // ignore failures to keep app resilient
  }
}

export function identify(id: string, props?: Props): void {
  const w = globalThis as AnalyticsWindow;
  const {gtag} = w;

  if (typeof gtag === 'function' && googleAnalyticsId) {
    gtag('config', googleAnalyticsId, {user_id: id}); // eslint-disable-line @typescript-eslint/naming-convention
    const clean = sanitizeProps(props);
    if (Object.keys(clean).length > 0) {
      gtag('set', 'user_properties', clean);
    }
  } else if (w.posthog?.identify) {
    // Keep PostHog fallback behavior
    w.posthog.identify(id, props);
  } else if (process.env.NODE_ENV !== 'production') {
    console.debug('[analytics] identify', id, props ?? {});
  }
}

export function track(name: string, props?: Props): void {
  const w = globalThis as AnalyticsWindow;
  const {gtag} = w;

  if (typeof gtag === 'function') {
    gtag('event', name, sanitizeProps(props));
  } else if (w.posthog?.capture) {
    // Keep PostHog fallback behavior
    w.posthog.capture(name, props ?? {});
  } else if (process.env.NODE_ENV !== 'production') {
    console.debug('[analytics] track', name, props ?? {});
  }
}

export function canonicalPath(pathname: string): string {
  // Aggregate resume preview pages: /resumes/preview/:id -> /resumes/preview
  if (/^\/resumes\/preview\/[^/]+$/.test(pathname)) {
    return '/resumes/preview';
  }

  // Aggregate resume detail/edit: /resumes/:id -> /resumes/[id]
  if (/^\/resumes\/[^/]+$/.test(pathname)) {
    return '/resumes/[id]';
  }

  // Jobs: keep /jobs/ai and /jobs/new as-is; aggregate specific ids to /jobs/[id]
  if (
    /^\/jobs\/[^/]+$/.test(pathname) &&
    !/^\/jobs\/(ai|new)$/.test(pathname)
  ) {
    return '/jobs/[id]';
  }

  // Default: unchanged
  return pathname;
}

export function trackPageview(_urlOverride?: string): void {
  const w = globalThis as AnalyticsWindow;
  const {gtag} = w;

  if (typeof gtag !== 'function') {
    if (process.env.NODE_ENV !== 'production') {
      const {pathname, search} = globalThis.location;
      const url = pathname + search;

      console.debug('[analytics] pageview', url);
    }

    return;
  }

  // Use the real URL for page_location; use canonicalPath for page_path aggregation
  const {origin, pathname, search} = globalThis.location;
  const pagePath = canonicalPath(pathname);
  const pageLocation = origin + pathname + search;

  gtag('event', 'page_view', {
    page_title: document.title, // eslint-disable-line @typescript-eslint/naming-convention
    page_location: pageLocation, // eslint-disable-line @typescript-eslint/naming-convention
    page_path: pagePath, // eslint-disable-line @typescript-eslint/naming-convention
  });
}
